import React, { useEffect, useState, useContext } from 'react';
import '../styles/Posts.css';
import '../styles/Comment.css';
import { formatTimeCompact } from '../utils/formatTimeCompact';
import { getHeartIcon } from '../utils/getThemeIcon';
import { getAuthorIconUrl } from '../utils/getAuthorPlaceholder';
import { ThemeContext } from '../context/ThemeContext';
import NewDiscussion from './NewDiscussion';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import useAuthState from '../hooks/useAuthState';
import useAuth from '../hooks/useAuth';

import { ApiContext } from '../context/ApiContext';
import apiCall from '../utils/apiCall';
import { useDemo } from '../context/DemoContext';

const Post = () => {
    const { isAuthenticated } = useAuthState();
    const location = useLocation();
    const { id } = useParams();
    const { initialPost } = location.state || {};
    const apiUrl = useContext(ApiContext);
    const { theme } = useContext(ThemeContext);
    const navigate = useNavigate();
    const [likedPostIds, setLikedPostIds] = useState([]);
    const [likedCommentIds, setLikedCommentIds] = useState([]);
    const [likesTrigger, setLikesTrigger] = useState(false);
    const [commentLikesTrigger, setCommentLikesTrigger] = useState(false);
    const [post, setPost] = useState({});
    const [comments, setComments] = useState([]);
    const [showNewDiscussion, setShowNewDiscussion] = useState(false);
    const [showPostMenu, setShowPostMenu] = useState(false);
    const [showCommentMenus, setShowCommentMenus] = useState({});
    const [newCommentContent, setNewCommentContent] = useState('');
    const [showDiscussions, setShowDiscussions] = useState(false);
    const [discussions, setDiscussions] = useState([]);
    const [expandedComments, setExpandedComments] = useState({});
    const [expandedPosts, setExpandedPosts] = useState({});
    const { getDiscussionsByParent, createComment } = useAuth();
    const { isDemoMode, getDemoPost, getDemoComments, demoLikedPostIds, demoLikedCommentIds, likePost: demoLikePost, likeComment: demoLikeComment, addComment: demoAddComment } = useDemo();



    // Helper function to count lines in post content
    const countPostLines = (text) => {
        if (!text) return 0;
        return text.split('\n').length;
    };

    // Helper function to toggle post expansion
    const togglePostExpansion = (postId) => {
        setExpandedPosts(prev => ({
            ...prev,
            [postId]: !prev[postId]
        }));
    };
    const handleLikePost = async (postId) => {
        if (isDemoMode) {
            demoLikePost(postId);
            return;
        }
        try {
            const response = await apiCall(`${apiUrl}/api/v1/post/like`, 'POST', { id: postId });
            if (response.ok) {
                setLikesTrigger(!likesTrigger);
            } else {
                console.error('Failed to like post');
            }
        } catch (error) {
            console.error('Error while liking post:', error);
        }
    };
    const handleLikeComment = async (postId, commentId) => {
        if (isDemoMode) {
            demoLikeComment(commentId);
            return;
        }
        try {
            const response = await apiCall(`${ apiUrl }/api/v1/post/comment/like`, 'POST', { postId: postId, commentId: commentId });
            if (response.ok) {
                setCommentLikesTrigger(!commentLikesTrigger);
            } else {
                console.error('Failed to like post');
            }
        } catch (error) {
            console.error('Error while liking post:', error);
        }
    };

    useEffect(() => {
        const fetchPosts = async () => {
            if (isDemoMode) {
                const postIdToUse = id || (initialPost && initialPost.id);
                const demoPost = getDemoPost(postIdToUse);
                if (demoPost) setPost(demoPost);
                return;
            }
            try {
                const response = await apiCall(`${apiUrl}/api/v1/post/${initialPost.id}`, 'GET');
                if (response.ok) {
                    const data = await response.json();
                    setPost(data);
                } else {
                    console.error('Failed to fetch posts');
                }
            } catch (error) {
                console.error('Error while fetching posts:', error);
            }
        };

        fetchPosts();
    }, [apiUrl, likesTrigger, initialPost, getDemoPost]);

    useEffect(() => {
        const fetchComments = async () => {
            if (isDemoMode) {
                const postIdToUse = id || (initialPost && initialPost.id);
                if (postIdToUse) setComments(getDemoComments(postIdToUse));
                return;
            }
            try {
                const postIdToUse = id || (initialPost && initialPost.id);
                if (postIdToUse) {
                    const response = await apiCall(`${apiUrl}/api/v1/post/${postIdToUse}/comment/list`, 'GET');
                    if (response.ok) {
                        const data = await response.json();
                        setComments(data);
                    } else {
                        console.error('Failed to fetch comments');
                    }
                }
            } catch (error) {
                console.error('Error while fetching comments:', error);
            }
        };

        fetchComments();
    }, [apiUrl, initialPost, commentLikesTrigger, id, getDemoComments]);

    useEffect(() => {
        const fetchDiscussions = async () => {
            if (isDemoMode) {
                setDiscussions([]);
                return;
            }
            try {
                const postIdToUse = id || (initialPost && initialPost.id);
                if (postIdToUse) {
                    const response = await getDiscussionsByParent('post', postIdToUse);
                    if (response.ok) {
                        const data = await response.json();
                        setDiscussions(data);
                    }
                }
            } catch (error) {
                console.error('Error while fetching discussions:', error);
            }
        };

        fetchDiscussions();
    }, [id, initialPost]);

    // Helper function to count lines in comment content
    const countLines = (text) => {
        if (!text) return 0;
        return text.split('\n').length;
    };

    // Helper function to toggle comment expansion
    const toggleCommentExpansion = (commentId) => {
        setExpandedComments(prev => ({
            ...prev,
            [commentId]: !prev[commentId]
        }));
    };

    // Close post and comment menus when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Close post menu if clicking outside
            if (showPostMenu && event.target.closest('.open-post-hamburger-menu') === null && event.target.closest('.open-post-menu-dropdown') === null) {
                setShowPostMenu(false);
            }
            
            // Close any open comment menus
            Object.keys(showCommentMenus).forEach(commentId => {
                if (showCommentMenus[commentId] && 
                    event.target.closest(`.comment-hamburger-menu-${commentId}`) === null && 
                    event.target.closest(`.comment-menu-dropdown`) === null) {
                    setShowCommentMenus(prev => ({...prev, [commentId]: false}));
                }
            });
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [showPostMenu, showCommentMenus]);

    const handleDiscussionAdded = async () => {
        const postIdToUse = id || (initialPost && initialPost.id);
        if (postIdToUse) {
            const response = await getDiscussionsByParent('post', postIdToUse);
            if (response.ok) {
                const updatedDiscussions = await response.json();
                setDiscussions(updatedDiscussions);
            }
        }
        setShowNewDiscussion(false);
    };

    const handleAddComment = async () => {
        if (!newCommentContent.trim()) return;
        
        const postIdToUse = id || (initialPost && initialPost.id);
        if (!postIdToUse) return;

        if (isDemoMode) {
            const newComment = demoAddComment(postIdToUse, newCommentContent);
            setComments(prev => [...prev, newComment]);
            setPost(prev => ({ ...prev, commentsCount: prev.commentsCount + 1 }));
            setNewCommentContent('');
            return;
        }
        
        try {
            // Map frontend data to backend expected format
            const commentData = {
                content: newCommentContent,
                parentId: postIdToUse
            };

            const response = await createComment(commentData);

            if (response.ok) {
                setNewCommentContent('');
                // Refresh comments to show the new one
                const commentsResponse = await apiCall(`${apiUrl}/api/v1/post/${postIdToUse}/comment/list`, 'GET');
                if (commentsResponse.ok) {
                    const data = await commentsResponse.json();
                    setComments(data);
                }
            } else {
                console.error('Failed to add comment');
            }
        } catch (error) {
            console.error('Error while adding comment:', error);
        }
    };

    useEffect(() => {
        const fetchLikedPosts = async () => {
            if (isDemoMode) {
                setLikedPostIds(demoLikedPostIds);
                return;
            }
            if (!post.id) { 
                console.error('Post ID is not available for fetching liked posts');
                return; 
            }
            if (!isAuthenticated) {
                setLikedPostIds([]);
                return;
            }
            const likedResponse = await apiCall(`${apiUrl}/api/v1/post/liked`, 'POST', { ids: [post.id] });
            if (likedResponse.ok) {
                const likedData = await likedResponse.json();
                setLikedPostIds(likedData.ids);
            } else {
                console.error('Failed to fetch liked posts');
            }
        }

        fetchLikedPosts();
    }, [apiUrl, post, isAuthenticated, demoLikedPostIds]);

    useEffect(() => {
        const fetchLikedComments = async () => {
            if (isDemoMode) {
                setLikedCommentIds(demoLikedCommentIds);
                return;
            }
            if (!post.id) { 
                console.error('Post ID is not available for fetching liked posts');
                return; 
            }
            if (!isAuthenticated || comments.length === 0) {
                setLikedCommentIds([]);
                return;
            }
            const likedResponse = await apiCall(`${apiUrl}/api/v1/post/comment/liked`, 'POST', { postId: post.id, commentIds: comments.map((comment) => comment.id) });
            if (likedResponse.ok) {
                const likedData = await likedResponse.json();
                setLikedCommentIds(likedData.ids);
            } else {
                console.error('Failed to fetch liked comments');
            }
        }

        fetchLikedComments();
    }, [apiUrl, post, comments, isAuthenticated, demoLikedCommentIds]);

    return (
                (!post || Object.keys(post).length === 0) ? (
                    <p></p>
                ) : (
                    <>
                        {/* <Comment postId={ post.id } onCommenting = {() => setCommenting(true)  } onBack={ handleBackFromComment }/> */}
                        <div className="post-container">
                            <div className="open-post-item">
                                <div className="open-post-header">
                                    <img src={getAuthorIconUrl(post, apiUrl, theme)} alt="Author" className="open-post-author-icon" />
                                    <div className="open-post-author-info">
                                        <p className="open-post-author">@{post.authorUsername}</p>
                                    </div>
                                    <p className="open-post-date">{formatTimeCompact(post.createdAt)}</p>
                                </div>
                                <div className="open-post-content-container">
                                    {countPostLines(post.content) > 10 && !expandedPosts[post.id] ? (
                                        <>
                                            <p className="open-post-content open-post-content-collapsed">
                                                {post.content.split('\n').slice(0, 10).join('\n')}
                                            </p>
                                            <div className="open-post-expand-button-container">
                                                <button
                                                    className="open-post-expand-button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        togglePostExpansion(post.id);
                                                    }}
                                                >
                                                    ...show more
                                                </button>
                                            </div>
                                        </>
                                    ) : (
                                        <p className="open-post-content">
                                            {post.content}
                                        </p>
                                    )}
                                    {countPostLines(post.content) > 10 && expandedPosts[post.id] && (
                                        <div className="open-post-expand-button-container">
                                            <button
                                                className="open-post-expand-button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    togglePostExpansion(post.id);
                                                }}
                                            >
                                                show less
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <div className="open-post-actions-container">
                                    <div className="open-post-like-action">
                                        <img
                                            src={getHeartIcon(theme, likedPostIds.includes(post.id))}
                                            alt="Like"
                                            className="open-post-like-icon"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleLikePost(post.id);
                                            }}
                                        />
                                        <span>{post.likesCount}</span>
                                    </div>
                                    <div className="open-post-hamburger-menu">
                                        <div
                                            className="post-menu-icon"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setShowPostMenu(!showPostMenu);
                                            }}
                                            aria-label="Post options menu"
                                            role="button"
                                            tabIndex="0"
                                        >
                                            <div className="three-dots-icon"></div>
                                        </div>
                                        {showPostMenu && (
                                            <div className="open-post-menu-dropdown">
                                                    {/* <div */}
                                                    {/*     className="open-post-menu-item" */}
                                                    {/*     onClick={(e) => { */}
                                                    {/*         e.stopPropagation(); */}
                                                    {/*         setShowNewDiscussion(true); */}
                                                    {/*         setShowPostMenu(false); */}
                                                    {/*     }} */}
                                                    {/* > */}
                                                    {/*     Reply */}
                                                    {/* </div> */}
                                                    <div
                                                        className="open-post-menu-item"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setShowNewDiscussion(true);
                                                            setShowPostMenu(false);
                                                        }}
                                                    >
                                                        Start Discussion
                                                    </div>
                                                </div>
                                        )}
                                    </div>
                                </div>
                            <div className="discussions-section-minimal">
                                <button
                                    className="discussions-toggle-minimal"
                                    onClick={() => setShowDiscussions(!showDiscussions)}
                                >
                                    {discussions.length > 0 ? (
                                        <>
                                            <span className="discussions-count">{discussions.length}</span>
                                            {showDiscussions ? ' Hide discussions' : ' Show discussions'}
                                        </>
                                    ) : (
                                        'No discussions yet'
                                    )}
                                </button>
                                {showDiscussions && discussions.length > 0 && (
                                    <div className="discussions-list-minimal">
                                        {discussions.map(discussion => (
                                            <div 
                                                key={discussion.id} 
                                                className="discussion-item-minimal"
                                                onClick={() => navigate(`/discussion/${discussion.id}`)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <div className="discussion-header-minimal">
                                                    <span className="discussion-author-minimal">@{discussion.authorUsername}</span>
                                                    <span className="discussion-date-minimal">{formatTimeCompact(discussion.createdAt)}</span>
                                                </div>
                                                <p className="discussion-content-minimal">{discussion.content}</p>
                                                {discussion.commentsCount > 0 && (
                                                    <div className="discussion-comments-minimal">
                                                        {discussion.commentsCount} {discussion.commentsCount === 1 ? 'comment' : 'comments'}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            </div>
                            <div className="open-post-comments-container">
                                <ul>
                                    {comments.map((comment) => (
                                        <li 
                                            key={comment.id} 
                                            className="comment-item"
                                        >
                                            <img src={getAuthorIconUrl(comment, apiUrl, theme)} alt="Author" className="comment-author-icon" />
                                            <div className="comment-body">
                                                <div className="comment-header">
                                                    <span className="comment-author">@{comment.authorUsername}</span>
                                                    <span className="comment-date">{formatTimeCompact(comment.createdAt)}</span>
                                                </div>
                                                <div className="comment-content-container">
                                                    {countLines(comment.content) > 5 && !expandedComments[comment.id] ? (
                                                        <>
                                                            <p className="comment-content comment-content-collapsed">
                                                                {comment.content.split('\n').slice(0, 5).join('\n')}
                                                            </p>
                                                            <div className="comment-expand-button-container">
                                                                <button
                                                                    className="comment-expand-button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        toggleCommentExpansion(comment.id);
                                                                    }}
                                                                >
                                                                    ...show more
                                                                </button>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <p className="comment-content">
                                                            {comment.content}
                                                        </p>
                                                    )}
                                                    {expandedComments[comment.id] && countLines(comment.content) > 5 && (
                                                        <div className="comment-expand-button-container">
                                                            <button
                                                                className="comment-expand-button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleCommentExpansion(comment.id);
                                                                }}
                                                            >
                                                                show less
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="comment-actions-container">
                                                    <div className="comment-like-action">
                                                        <img
                                                            src={getHeartIcon(theme, likedCommentIds.includes(comment.id))}
                                                            alt="Like"
                                                            className="comment-like-icon"
                                                            onClick={(e) => {
                                                                e.stopPropagation(); 
                                                                handleLikeComment(post.id, comment.id);
                                                            }}
                                                        />
                                                        <span>{ comment.likesCount }</span>
                                                    </div>
                                                    <div className={`comment-hamburger-menu comment-hamburger-menu-${comment.id}`}>
                                                        <div
                                                            className="post-menu-icon"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setShowCommentMenus(prev => ({...prev, [comment.id]: !prev[comment.id]}));
                                                            }}
                                                            aria-label="Comment options menu"
                                                            role="button"
                                                            tabIndex="0"
                                                        >
                                                            <div className="three-dots-icon"></div>
                                                        </div>
                                                        {showCommentMenus[comment.id] && (
                                                            <>
                                                                <div className="comment-menu-dropdown">
                                                                    <div
                                                                        className="comment-menu-item comment-menu-item-disabled"
                                                                        style={{ opacity: 0.5, cursor: 'not-allowed' }}
                                                                    >
                                                                        Reply (coming soon)
                                                                    </div>
                                                                    <div
                                                                        className="comment-menu-item comment-menu-item-disabled"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setShowNewDiscussion(true);
                                                                            setShowCommentMenus(prev => ({...prev, [comment.id]: false}));
                                                                        }}
                                                                    >
                                                                        Start Discussion (coming soon)
                                                                    </div>
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                        <div className="comment-input-area">
                            <div className="comment-input-container">
                                <textarea
                                    ref={(textarea) => {
                                        if (textarea) {
                                            // Auto-resize textarea to fit content
                                            textarea.style.height = 'auto';
                                            textarea.style.height = `${Math.min(Math.max(textarea.scrollHeight, 40), 200)}px`;
                                        }
                                    }}
                                    value={newCommentContent}
                                    onChange={(e) => {
                                        setNewCommentContent(e.target.value);
                                        // Auto-resize on content change
                                        const textarea = e.target;
                                        textarea.style.height = 'auto';
                                        textarea.style.height = `${Math.min(Math.max(textarea.scrollHeight, 40), 200)}px`;
                                    }}
                                    placeholder="Add a comment..."
                                    className="comment-input"
                                    rows="1"
                                    onKeyDown={(e) => {
                                        // Allow Enter to add new lines (default behavior)
                                        // Only submit on Ctrl+Enter or Cmd+Enter
                                        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                                            e.preventDefault();
                                            handleAddComment();
                                        }
                                    }}
                                />
                                <button
                                    className="comment-reply-button"
                                    onClick={handleAddComment}
                                    disabled={!newCommentContent.trim()}
                                >
                                    Reply
                                </button>
                            </div>
                        </div>
                        {showNewDiscussion && (
                            <NewDiscussion
                                postId={post.id}
                                onDiscussionAdded={handleDiscussionAdded}
                                onCancel={() => setShowNewDiscussion(false)}
                            />
                        )}
                    </>
                )
    );
};

export default Post;
